var __values = this && this.__values || function (o) {
  var s = typeof Symbol === "function" && Symbol.iterator,
      m = s && o[s],
      i = 0;
  if (m) return m.call(o);
  if (o && typeof o.length === "number") return {
    next: function next() {
      if (o && i >= o.length) o = void 0;
      return {
        value: o && o[i++],
        done: !o
      };
    }
  };
  throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};

import { DataAttribute, dataToDatasetAttributeName, getDataAttributesFromEventPath } from '../utils/dataAttributes';
import { EventManager } from './EventManager';
import { HighlighterContainerTag, HighlighterElementTag, HighlightRenderer } from './HighlightRenderer';
export var MinimumVisiblePartForHighlight = 0.5;
export var NodeSmartLinkProviderEventType;

(function (NodeSmartLinkProviderEventType) {
  NodeSmartLinkProviderEventType["ElementClicked"] = "kontent-smart-link:element:clicked";
})(NodeSmartLinkProviderEventType || (NodeSmartLinkProviderEventType = {}));

var NodeSmartLinkProvider =
/** @class */
function () {
  function NodeSmartLinkProvider() {
    var _this = this;

    this.enabled = false;
    this.renderingTimeoutId = 0;
    this.observedElements = new Set();
    this.visibleElements = new Set();

    this.destroy = function () {
      _this.disable();

      _this.renderer.destroy();
    };

    this.addEventListener = function (type, listener) {
      _this.events.on(type, listener);
    };

    this.removeEventListener = function (type, listener) {
      _this.events.off(type, listener);
    };

    this.enable = function () {
      if (_this.enabled) return;

      _this.startRenderingInterval();

      _this.listenToGlobalEvents();

      _this.observeDomMutations();

      _this.enabled = true;
    };

    this.disable = function () {
      if (!_this.enabled) return;

      _this.stopRenderingInterval();

      _this.unlistenToGlobalEvents();

      _this.disconnectObservers();

      _this.renderer.clear();

      _this.enabled = false;
    };

    this.highlightVisibleElements = function () {
      requestAnimationFrame(function () {
        _this.renderer.render(_this.visibleElements);
      });
    };
    /**
     * Start an interval rendering (1s) that will re-render highlights for all visible elements using `setTimeout`.
     * It helps to adjust highlights position even in situations that are currently not supported by
     * the SDK (e.g. element position change w/o animations, some infinite animations and other possible unhandled cases)
     * for better user experience.
     */


    this.startRenderingInterval = function () {
      _this.highlightVisibleElements();

      _this.renderingTimeoutId = setTimeout(_this.startRenderingInterval, 1000);
    };

    this.stopRenderingInterval = function () {
      if (_this.renderingTimeoutId) {
        clearTimeout(_this.renderingTimeoutId);
        _this.renderingTimeoutId = 0;
      }
    };

    this.listenToGlobalEvents = function () {
      window.addEventListener('scroll', _this.highlightVisibleElements, {
        capture: true
      });
      window.addEventListener('resize', _this.highlightVisibleElements, {
        passive: true
      });
      window.addEventListener('animationend', _this.highlightVisibleElements, {
        passive: true,
        capture: true
      });
      window.addEventListener('transitionend', _this.highlightVisibleElements, {
        passive: true,
        capture: true
      });
      window.addEventListener('click', _this.onElementClick, {
        capture: true
      });
    };

    this.unlistenToGlobalEvents = function () {
      window.removeEventListener('scroll', _this.highlightVisibleElements, {
        capture: true
      });
      window.removeEventListener('resize', _this.highlightVisibleElements);
      window.removeEventListener('animationend', _this.highlightVisibleElements, {
        capture: true
      });
      window.removeEventListener('transitionend', _this.highlightVisibleElements, {
        capture: true
      });
      window.removeEventListener('click', _this.onElementClick, {
        capture: true
      });
    };

    this.observeDomMutations = function () {
      if (_this.enabled) return;
      document.querySelectorAll("*[" + DataAttribute.ElementCodename + "]").forEach(function (element) {
        if (element instanceof HTMLElement) {
          _this.observeElementVisibility(element);
        }
      });

      _this.mutationObserver.observe(window.document.body, {
        childList: true,
        subtree: true
      });
    };

    this.disconnectObservers = function () {
      _this.mutationObserver.disconnect();

      _this.intersectionObserver.disconnect();

      _this.observedElements.forEach(function (element) {
        _this.unobserveElementVisibility(element);
      });

      _this.observedElements = new Set();
      _this.visibleElements = new Set();
    };

    this.observeElementVisibility = function (element) {
      if (_this.observedElements.has(element)) return;

      _this.intersectionObserver.observe(element);

      _this.observedElements.add(element);

      element.addEventListener('mousemove', _this.onElementMouseEnter);
      element.addEventListener('mouseleave', _this.onElementMouseLeave);
    };

    this.unobserveElementVisibility = function (element) {
      if (!_this.observedElements.has(element)) return;

      _this.intersectionObserver.unobserve(element);

      _this.observedElements.delete(element);

      _this.visibleElements.delete(element);

      element.removeEventListener('mousemove', _this.onElementMouseEnter);
      element.removeEventListener('mouseleave', _this.onElementMouseLeave);
    };

    this.onDomMutation = function (mutations) {
      var e_1, _a, e_2, _b, e_3, _c, e_4, _d, e_5, _e;

      var attrName = dataToDatasetAttributeName(DataAttribute.ElementCodename);
      var relevantMutations = mutations.filter(function (mutation) {
        return mutation.target instanceof Element && mutation.type === 'childList' && ![HighlighterElementTag, HighlighterContainerTag].includes(mutation.target.tagName) && Array.from(mutation.addedNodes).some(function (node) {
          return node.tagName !== HighlighterElementTag;
        }) && Array.from(mutation.removedNodes).some(function (node) {
          return node.tagName !== HighlighterElementTag;
        });
      });

      try {
        for (var relevantMutations_1 = __values(relevantMutations), relevantMutations_1_1 = relevantMutations_1.next(); !relevantMutations_1_1.done; relevantMutations_1_1 = relevantMutations_1.next()) {
          var mutation = relevantMutations_1_1.value;

          try {
            for (var _f = (e_2 = void 0, __values(mutation.addedNodes)), _g = _f.next(); !_g.done; _g = _f.next()) {
              var node = _g.value;
              if (!(node instanceof HTMLElement)) continue;

              if (node.dataset[attrName]) {
                _this.observeElementVisibility(node);
              }

              try {
                for (var _h = (e_3 = void 0, __values(node.querySelectorAll("*[" + DataAttribute.ElementCodename + "]"))), _j = _h.next(); !_j.done; _j = _h.next()) {
                  var element = _j.value;
                  if (!(element instanceof HTMLElement)) continue;

                  _this.observeElementVisibility(element);
                }
              } catch (e_3_1) {
                e_3 = {
                  error: e_3_1
                };
              } finally {
                try {
                  if (_j && !_j.done && (_c = _h.return)) _c.call(_h);
                } finally {
                  if (e_3) throw e_3.error;
                }
              }
            }
          } catch (e_2_1) {
            e_2 = {
              error: e_2_1
            };
          } finally {
            try {
              if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
            } finally {
              if (e_2) throw e_2.error;
            }
          }

          try {
            for (var _k = (e_4 = void 0, __values(mutation.removedNodes)), _l = _k.next(); !_l.done; _l = _k.next()) {
              var node = _l.value;
              if (!(node instanceof HTMLElement)) continue;

              if (node.dataset[attrName]) {
                _this.unobserveElementVisibility(node);
              }

              try {
                for (var _m = (e_5 = void 0, __values(node.querySelectorAll("*[" + DataAttribute.ElementCodename + "]"))), _o = _m.next(); !_o.done; _o = _m.next()) {
                  var element = _o.value;
                  if (!(element instanceof HTMLElement)) continue;

                  _this.unobserveElementVisibility(element);
                }
              } catch (e_5_1) {
                e_5 = {
                  error: e_5_1
                };
              } finally {
                try {
                  if (_o && !_o.done && (_e = _m.return)) _e.call(_m);
                } finally {
                  if (e_5) throw e_5.error;
                }
              }
            }
          } catch (e_4_1) {
            e_4 = {
              error: e_4_1
            };
          } finally {
            try {
              if (_l && !_l.done && (_d = _k.return)) _d.call(_k);
            } finally {
              if (e_4) throw e_4.error;
            }
          }
        }
      } catch (e_1_1) {
        e_1 = {
          error: e_1_1
        };
      } finally {
        try {
          if (relevantMutations_1_1 && !relevantMutations_1_1.done && (_a = relevantMutations_1.return)) _a.call(relevantMutations_1);
        } finally {
          if (e_1) throw e_1.error;
        }
      }

      if (relevantMutations.length > 0) {
        _this.highlightVisibleElements();
      }
    };

    this.onElementVisibilityChange = function (entries) {
      var e_6, _a;

      var filteredEntries = entries.filter(function (entry) {
        return entry.target instanceof HTMLElement;
      });

      try {
        for (var filteredEntries_1 = __values(filteredEntries), filteredEntries_1_1 = filteredEntries_1.next(); !filteredEntries_1_1.done; filteredEntries_1_1 = filteredEntries_1.next()) {
          var entry = filteredEntries_1_1.value;
          var target = entry.target;

          if (entry.isIntersecting && entry.intersectionRatio >= MinimumVisiblePartForHighlight) {
            _this.visibleElements.add(target);
          } else {
            _this.visibleElements.delete(target);
          }
        }
      } catch (e_6_1) {
        e_6 = {
          error: e_6_1
        };
      } finally {
        try {
          if (filteredEntries_1_1 && !filteredEntries_1_1.done && (_a = filteredEntries_1.return)) _a.call(filteredEntries_1);
        } finally {
          if (e_6) throw e_6.error;
        }
      }

      if (filteredEntries.length > 0) {
        _this.highlightVisibleElements();
      }
    };

    this.onElementClick = function (event) {
      var attributes = getDataAttributesFromEventPath(event);

      if (attributes.has(DataAttribute.ElementCodename)) {
        event.preventDefault();

        _this.events.emit(NodeSmartLinkProviderEventType.ElementClicked, {
          projectId: attributes.get(DataAttribute.ProjectId),
          languageCodename: attributes.get(DataAttribute.LanguageCodename),
          itemId: attributes.get(DataAttribute.ItemId),
          elementCodename: attributes.get(DataAttribute.ElementCodename)
        });
      }
    };

    this.onElementMouseEnter = function (event) {
      var node = event.currentTarget;

      _this.renderer.selectNode(node);
    };

    this.onElementMouseLeave = function (event) {
      var node = event.currentTarget;

      _this.renderer.deselectNode(node);
    };

    this.mutationObserver = new MutationObserver(this.onDomMutation);
    this.intersectionObserver = new IntersectionObserver(this.onElementVisibilityChange, {
      threshold: [MinimumVisiblePartForHighlight]
    });
    this.events = new EventManager();
    this.renderer = new HighlightRenderer();
  }

  return NodeSmartLinkProvider;
}();

export { NodeSmartLinkProvider };